import React, { ReactNode } from 'react';
import {
  Icon,
  IconName,
  IconVariant,
  joinClassNames,
  OneOrMoreReactElements,
} from '@estimateone/frontend-components';
import moment from 'moment-timezone';
import { getUserTimezone } from '@ascension/js/app/timezone';
import styles from './styles.scss';

export enum TimelineSize {
  Small = 'small',
  Large = 'large',
}

type TimelineDotProps = { radius: number };
const TimelineDot = ({ radius }: TimelineDotProps) => (
  <svg width={radius * 2} height={radius * 2} className={styles.dot}>
    <circle cx={radius} cy={radius} r={radius - 1} />
  </svg>
);

type TimelineSegmentProps = {
  children: ReactNode;
  isHighlighted?: boolean;
  timestamp?: string;
  iconName?: IconName;
  dateTimeFormat?: string;
};

const TimelineSegment = ({
  children,
  isHighlighted = false,
  timestamp,
  iconName,
  dateTimeFormat = 'DD/MM/YYYY',
}: TimelineSegmentProps) => (
  <li className={joinClassNames(styles.timelineSegment, isHighlighted && styles.highlighted)}>
    {timestamp && (
      <div className={joinClassNames(styles.section, styles.date)}>
        {moment(timestamp).isValid()
          ? moment(timestamp).tz(getUserTimezone()).format(dateTimeFormat)
          : timestamp}
      </div>
    )}
    <div className={`${styles.section} ${styles.connector}`}>
      {iconName ? (
        <Icon name={iconName} size="14" variant={IconVariant.Blue} />
      ) : (
        <TimelineDot radius={6} />
      )}
      <span className={joinClassNames(styles.line, iconName !== undefined && styles.iconLine)} />
    </div>
    <div className={styles.section}>{children}</div>
  </li>
);

type TimelineProps = {
  children: OneOrMoreReactElements<TimelineSegmentProps>;
  fullWidth?: boolean;
  size?: TimelineSize;
};

const Timeline = ({ children, fullWidth = false, size = TimelineSize.Small }: TimelineProps) => (
  <ul
    className={joinClassNames(
      styles.timeline,
      styles[size as keyof typeof styles],
      fullWidth && styles.fullWidth,
    )}
  >
    {children}
  </ul>
);

// eslint-disable-next-line fp/no-mutation
Timeline.Segment = TimelineSegment;

export default Timeline;
